import { useEffect, useRef, useState } from "react";
import { Globe, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage, type Lang } from "@/i18n/LanguageContext";

interface LanguageSwitcherProps {
  /** "dark" = white text on dark header, "light" = black text on light overlay header */
  tone?: "dark" | "light";
  compact?: boolean;
  className?: string;
}

const OPTIONS: { code: Lang; label: string }[] = [
  { code: "en", label: "English" },
  { code: "ru", label: "RU — Русский" },
];

export const LanguageSwitcher = ({ tone = "dark", compact = false, className }: LanguageSwitcherProps) => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const isLight = tone === "light";

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("Язык")}
        aria-expanded={open}
        className={cn(
          "group inline-flex items-center gap-1.5 rounded-full border transition-colors duration-300",
          compact ? "px-2 py-[3px]" : "px-2.5 py-1",
          isLight
            ? "border-black/15 text-black/60 hover:border-black/35 hover:text-black"
            : "border-white/15 text-white/70 hover:border-white/40 hover:text-white",
        )}
      >
        <Globe className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} strokeWidth={1.6} />
        <span
          className={cn(
            "font-medium uppercase tracking-[0.12em]",
            compact ? "text-[8.5px]" : "text-[10.5px]",
          )}
        >
          {lang === "en" ? "EN" : "RU"}
        </span>
      </button>

      {open && (
        <div
          className={cn(
            "absolute right-0 top-[calc(100%+8px)] z-[200] min-w-[152px] overflow-hidden rounded-xl border shadow-[0_18px_40px_-20px_rgba(0,0,0,0.7)] backdrop-blur-md",
            isLight ? "border-black/10 bg-white/95" : "border-white/12 bg-[#0B0B0B]/95",
          )}
        >
          <p
            className={cn(
              "px-3 pt-2.5 pb-1 text-[9px] font-medium uppercase tracking-[0.24em]",
              isLight ? "text-black/35" : "text-white/35",
            )}
          >
            {t("Язык")}
          </p>
          {OPTIONS.map((o) => (
            <button
              key={o.code}
              type="button"
              onClick={() => {
                setLang(o.code);
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-[12.5px] transition-colors duration-200",
                isLight
                  ? lang === o.code
                    ? "bg-black/[0.05] text-black"
                    : "text-black/60 hover:bg-black/[0.04] hover:text-black"
                  : lang === o.code
                    ? "bg-white/[0.07] text-white"
                    : "text-white/60 hover:bg-white/[0.05] hover:text-white",
              )}
            >
              <span>{o.label}</span>
              {lang === o.code && <Check className="h-3.5 w-3.5" strokeWidth={2} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
